import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const FullscreenOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.95);
    z-index: 9999;
    display: flex;
    justify-content: center;
    align-items: center;
    backdrop-filter: blur(3px);
`;

const MediaContainer = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    cursor: ${props => props.isImage && !props.isZoomed ? 'zoom-in' : props.isImage && props.isZoomed ? 'grab' : 'default'};
    
    &:active {
        cursor: ${props => props.isImage && props.isZoomed ? 'grabbing' : 'default'};
    }
`;

const MediaElement = styled.div`
    transition: transform 0.2s ease;
    transform: ${props => `translate(${props.panX}px, ${props.panY}px) scale(${props.scale})`};
    transform-origin: center center;
    
    img, video {
        max-width: ${props => props.isZoomed ? 'none' : '90vw'};
        max-height: ${props => props.isZoomed ? 'none' : '90vh'};
        width: auto;
        height: auto;
        display: block;
    }
    
    video {
        max-width: 90vw;
        max-height: 90vh;
    }
`;

const NavigationButton = styled.button`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(255, 255, 255, 0.1);
    border: 2px solid rgba(255, 255, 255, 0.3);
    color: white;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    font-size: 20px;
    z-index: 10000;
    transition: all 0.2s ease;
    backdrop-filter: blur(10px);
    
    &:hover {
        background: rgba(255, 255, 255, 0.2);
        border-color: rgba(255, 255, 255, 0.5);
        transform: translateY(-50%) scale(1.1);
    }
    
    &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
        transform: translateY(-50%) scale(1);
    }
    
    ${props => props.direction === 'prev' ? 'left: 30px;' : 'right: 30px;'}
    
    @media (max-width: 768px) {
        width: 50px;
        height: 50px;
        font-size: 16px;
        ${props => props.direction === 'prev' ? 'left: 15px;' : 'right: 15px;'}
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 30px;
    right: 30px;
    background: rgba(255, 255, 255, 0.1);
    border: 2px solid rgba(255, 255, 255, 0.3);
    color: white;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    font-size: 24px;
    z-index: 10000;
    transition: all 0.2s ease;
    backdrop-filter: blur(10px);
    
    &:hover {
        background: rgba(255, 255, 255, 0.2);
        border-color: rgba(255, 255, 255, 0.5);
        transform: scale(1.1);
    }
    
    @media (max-width: 768px) {
        top: 15px;
        right: 15px;
        width: 40px;
        height: 40px;
        font-size: 20px;
    }
`;

const ZoomControls = styled.div`
    position: absolute;
    bottom: 30px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 10px;
    z-index: 10000;
    
    @media (max-width: 768px) {
        bottom: 15px;
    }
`;

const ZoomButton = styled.button`
    background: rgba(255, 255, 255, 0.1);
    border: 2px solid rgba(255, 255, 255, 0.3);
    color: white;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    font-size: 16px;
    transition: all 0.2s ease;
    backdrop-filter: blur(10px);
    
    &:hover {
        background: rgba(255, 255, 255, 0.2);
        border-color: rgba(255, 255, 255, 0.5);
        transform: scale(1.1);
    }
    
    &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
        transform: scale(1);
    }
`;

const MediaInfo = styled.div`
    position: absolute;
    bottom: 30px;
    right: 30px;
    color: white;
    background: rgba(0, 0, 0, 0.5);
    padding: 10px 15px;
    border-radius: 5px;
    backdrop-filter: blur(10px);
    font-size: 14px;
    z-index: 10000;
    
    @media (max-width: 768px) {
        bottom: 15px;
        right: 15px;
        font-size: 12px;
        padding: 8px 12px;
    }
`;

const PDFDownloadLink = styled.a`
    position: absolute;
    bottom: 100px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(255, 255, 255, 0.9);
    color: #333;
    padding: 12px 24px;
    border-radius: 25px;
    text-decoration: none;
    font-weight: 600;
    z-index: 10000;
    transition: all 0.2s ease;
    backdrop-filter: blur(10px);
    
    &:hover {
        background: rgba(255, 255, 255, 1);
        transform: translateX(-50%) scale(1.05);
        color: #333;
        text-decoration: none;
    }
    
    @media (max-width: 768px) {
        bottom: 80px;
        padding: 10px 20px;
        font-size: 14px;
    }
`;

const FullscreenMediaViewer = ({ 
    slides, 
    currentSlideIndex, 
    onClose, 
    onNext,
    onPrevious,
    isOpen 
}) => {
    const [scale, setScale] = useState(1);
    const [panX, setPanX] = useState(0);
    const [panY, setPanY] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [dragStartPan, setDragStartPan] = useState({ x: 0, y: 0 });
    
    const { i18n } = useTranslation('common');

    const currentSlide = slides[currentSlideIndex];
    const isImage = currentSlide?.type?.includes('image');
    const isVideo = currentSlide?.type?.includes('video');
    const isPDF = currentSlide?.type?.includes('pdf');
    const isZoomed = scale > 1;

    // Reset zoom and pan when slide changes
    useEffect(() => {
        setScale(1);
        setPanX(0);
        setPanY(0);
    }, [currentSlideIndex]);


    // Prevent body scroll when fullscreen is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    const goToNext = useCallback(() => {
        if (onNext && currentSlideIndex < slides.length - 1) {
            onNext();
        }
    }, [onNext, currentSlideIndex, slides.length]);

    const goToPrevious = useCallback(() => {
        if (onPrevious && currentSlideIndex > 0) {
            onPrevious();
        }
    }, [onPrevious, currentSlideIndex]);

    const zoomIn = useCallback(() => {
        if (!isImage) return;
        setScale(prev => Math.min(prev * 1.5, 5));
    }, [isImage]);

    const zoomOut = useCallback(() => {
        if (!isImage) return;
        setScale(prev => Math.max(prev / 1.5, 1));
        if (scale <= 1.5) {
            setPanX(0);
            setPanY(0);
        }
    }, [isImage, scale]);

    const resetZoom = useCallback(() => {
        if (!isImage) return;
        setScale(1);
        setPanX(0);
        setPanY(0);
    }, [isImage]);

    const handleMouseDown = (e) => {
        if (!isImage || !isZoomed) return;
        
        setIsDragging(true);
        setDragStart({ x: e.clientX, y: e.clientY });
        setDragStartPan({ x: panX, y: panY });
        e.preventDefault();
    };

    const handleMouseMove = (e) => {
        if (!isDragging || !isImage || !isZoomed) return;
        
        const deltaX = e.clientX - dragStart.x;
        const deltaY = e.clientY - dragStart.y;
        
        setPanX(dragStartPan.x + deltaX);
        setPanY(dragStartPan.y + deltaY);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleMediaClick = (e) => {
        if (!isImage) return;
        
        if (!isZoomed) {
            // Zoom in and center on click point
            const rect = e.currentTarget.getBoundingClientRect();
            const clickX = e.clientX - rect.left - rect.width / 2;
            const clickY = e.clientY - rect.top - rect.height / 2;
            
            setScale(2);
            setPanX(-clickX * 0.5);
            setPanY(-clickY * 0.5);
        }
    };

    const handleWheel = (e) => {
        if (!isImage) return;
        
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        const newScale = Math.min(Math.max(scale * delta, 1), 5);
        
        if (newScale <= 1) {
            setScale(1);
            setPanX(0);
            setPanY(0);
        } else {
            setScale(newScale);
        }
    };

    // Handle keyboard navigation (moved after function definitions)
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e) => {
            switch (e.key) {
                case 'Escape':
                    onClose();
                    break;
                case 'ArrowLeft':
                    goToPrevious();
                    break;
                case 'ArrowRight':
                    goToNext();
                    break;
                case '+':
                case '=':
                    if (isImage) zoomIn();
                    break;
                case '-':
                    if (isImage) zoomOut();
                    break;
                case '0':
                    if (isImage) resetZoom();
                    break;
                default:
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, isImage, onClose, goToNext, goToPrevious, zoomIn, zoomOut, resetZoom]);

    if (!isOpen || !currentSlide) return null;

    const filePath = process.env.REACT_APP_MEDIA_BASE_URL + "userfiles/" + currentSlide.path;
    const filename = filePath.split('/').pop();
    const pdfPreviewPath = filePath.replace(filename, "firstpage_" + filename).replace('.pdf', '.png');

    return (
        <FullscreenOverlay>
            <MediaContainer
                isImage={isImage}
                isZoomed={isZoomed}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onWheel={handleWheel}
            >
                <MediaElement
                    scale={scale}
                    panX={panX}
                    panY={panY}
                    isZoomed={isZoomed}
                    onClick={handleMediaClick}
                >
                    {isImage && (
                        <img 
                            src={filePath} 
                            alt={currentSlide.legende || ""} 
                            draggable={false}
                            style={{ backgroundColor: '#888' }}
                        />
                    )}
                    {isVideo && (
                        <video 
                            src={filePath} 
                            controls 
                            autoPlay={false}
                        />
                    )}
                    {isPDF && (
                        <img 
                            src={pdfPreviewPath} 
                            alt={currentSlide.legende || "PDF Preview"} 
                            draggable={false}
                        />
                    )}
                </MediaElement>
            </MediaContainer>

            {/* Close Button */}
            <CloseButton onClick={onClose}>
                ✕
            </CloseButton>

            {/* Navigation Buttons */}
            {slides.length > 1 && (
                <>
                    <NavigationButton 
                        direction="prev"
                        onClick={goToPrevious}
                        disabled={currentSlideIndex === 0}
                    >
                        ❮
                    </NavigationButton>
                    <NavigationButton 
                        direction="next"
                        onClick={goToNext}
                        disabled={currentSlideIndex === slides.length - 1}
                    >
                        ❯
                    </NavigationButton>
                </>
            )}

            {/* Zoom Controls for Images */}
            {isImage && (
                <ZoomControls>
                    <ZoomButton onClick={zoomOut} disabled={scale <= 1}>
                        −
                    </ZoomButton>
                    <ZoomButton onClick={resetZoom} disabled={scale === 1}>
                        ⌂
                    </ZoomButton>
                    <ZoomButton onClick={zoomIn} disabled={scale >= 5}>
                        +
                    </ZoomButton>
                </ZoomControls>
            )}

            {/* PDF Download Link */}
            {isPDF && (
                <PDFDownloadLink 
                    href={filePath} 
                    target="_blank" 
                    rel="noreferrer"
                >
                    {i18n.language === "fr" ? "Voir le document" : "View document"}
                </PDFDownloadLink>
            )}

            {/* Media Info */}
            <MediaInfo>
                {currentSlide.legende && (
                    <div style={{ marginBottom: '5px' }}>
                        {currentSlide.legende}
                    </div>
                )}
                {currentSlide.credit && (
                    <div style={{ fontSize: '12px', opacity: 0.8 }}>
                        © {currentSlide.credit}
                    </div>
                )}
                <div style={{ fontSize: '11px', opacity: 0.6, marginTop: '5px' }}>
                    {currentSlideIndex + 1} / {slides.length}
                </div>
            </MediaInfo>
        </FullscreenOverlay>
    );
};

export default FullscreenMediaViewer;